import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, LOCALE_ID, StaticProvider } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { BrowserDetection } from 'src/utils/browser';
import { Fonts } from 'src/utils/fonts';
import { Icons } from 'src/utils/icons';
import { StorageUtil } from 'src/utils/storage.util';
import { BASE_URL, GET_IP, LANGUAGE, LOG_SERVICE, WINDOW } from './boot.tokens';
import { Guard } from './guards';
import { CONTENT_CACHE, getContentCache } from '../navigation/tokens';


declare const InstallTrigger: any;
declare const __CONTENT_CACHE: { [key: string]: any };
declare const window;

export class InitialLoads {
    static LOCALE_ID: string;
    // tslint:disable-next-line: no-inferrable-types
    static DEFAULT_LANGUAGE: string = 'da-DK';

    static PROVIDERS: StaticProvider[] = [
        {
            provide: LANGUAGE,
            useFactory: InitialLoads.getLanguage,
            deps: [DOCUMENT]
        },
        {
            provide: CONTENT_CACHE,
            useFactory: getContentCache,
            deps: [DOCUMENT]
        },
        {
            provide: GET_IP,
            useFactory: BrowserDetection.getIP
        },
        {
            provide: BASE_URL,
            useFactory: InitialLoads.getBaseUrl,
        },
        {
            provide: WINDOW,
            useValue: window,
        },
        {
            provide: LOG_SERVICE,
            useValue: ({
                error: (_) => {
                }
            }),
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useValue: 'DKK',
        },
        {
            provide: APP_BASE_HREF,
            useValue: '/',
        },
        {
            provide: LOCALE_ID,
            useValue: InitialLoads.LOCALE_ID,
        },
    ];

    static async preload(preloadOptions?: { fonts: Fonts.Font[] }): Promise<unknown> {
        const contentCache = __CONTENT_CACHE;
        const options = contentCache.system;
        const scripts = contentCache.scripts;

        if (Guard.not.nullOrUndefined(contentCache))
            Object
                .keys(contentCache)
                .forEach(async key => StorageUtil.set(key, contentCache[key]));

        const loads: Promise<unknown>[] = [InitialLoads.setLocale().catch()];

        if (Array.isArray(options?.theme?.fonts))
            loads.push(Fonts.loadFont(options?.theme?.fonts).catch());

        if (Array.isArray(options?.theme?.svgs))
            loads.push(Icons.loadIcons(options?.theme?.svgs).catch());

        loads.push(InitialLoads.setColors(options?.theme).catch());

        if (/HeadlessChrome/.test(window.navigator.userAgent))
            return Promise.allSettled(loads);

        // if (Array.isArray(options?.theme?.paintMasks)) {
        //     Worklets.setPaintMasks(options?.theme?.paintMasks);
        // }
        // InitialLoads.setPaintMasks([]);
        if (Array.isArray(scripts) && document != null)
            scripts.forEach(s => document.head.append(InitialLoads.MapScript(s)))

        return Promise.allSettled(loads);
    }

    static MapScript(s: string): string | Node {
        if (s.includes('<script'))
            if (s.split('>')[0].includes('src='))
                s = s.split('src="')[1].split('"')[0];
            else {
                var startIndex = s.indexOf('>');
                var endIndex = s.lastIndexOf('<');
                s = s.substring(startIndex, endIndex + 1);
            }

        var scriptElm = document.createElement('script');

        if (s.startsWith('http')) {
            scriptElm.setAttribute('async', '');
            scriptElm.src = s;
        } else {
            scriptElm.innerHTML = s;
        }
        return scriptElm;
    }

    static getBaseUrl() {
        return window?.location?.origin;
    }

    static popState(router: Router): Observable<boolean> {
        return router
            .events
            .pipe(
                filter(event => event instanceof NavigationStart),
                map((event: NavigationStart) => event.navigationTrigger === 'popstate'),
                startWith(false),
                shareReplay(1),
            );
    }

    private static setColors(colors: { [x: string]: string; }[]): Promise<unknown> {
        colors = colors ?? [];
        const style = document.createElement('style');
        style.innerHTML = `:root \n{ ${Object.keys(colors).filter(key => key.startsWith('--')).map(key => `${key}: ${colors[key]};`).join('\n')}\n }`;
        document.head.prepend(style);
        return new Promise((resolve) => resolve({}));
    }

    private static getLanguage(document: Document): string {
        let lang = InitialLoads.DEFAULT_LANGUAGE;
        try {
            const documentLang = document.documentElement.lang;
            if (Guard.not.nullOrUndefined(documentLang)) {
                lang = documentLang;
            }
        } catch {
        }
        return lang.toLowerCase();
    }

    private static setLocale() {
        // tslint:disable-next-line: no-string-literal
        let currentLanguage = InitialLoads.DEFAULT_LANGUAGE;
        try {
            const documentLang = document.documentElement.lang;
            if (Guard.not.nullOrUndefined(documentLang)) {
                currentLanguage = documentLang;
            }
        } finally {
        }
        return InitialLoads
            .registerLocale(currentLanguage)
            .catch(() => InitialLoads
                .registerLocale(InitialLoads.DEFAULT_LANGUAGE)
            );
    }

    private static registerLocale(lang: string): Promise<void> {
        InitialLoads.LOCALE_ID = lang;
        return Promise.resolve();
    }
}
